import React, { FC, useEffect, useState } from "react";
import Modal from "../Modal";
import { FormFileUpload, FormTextInput } from "../FormFields";
import {
  PaymentInterface,
  PaymentMethodEnum,
  PaymentStatusEnum,
} from "../../interfaces";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { PrimaryButton, SecondaryButton } from "../Buttons";
import { currencyExchangeText, useCurrencyExchanges } from "../../utils";
import { UserInterface } from "../../interfaces/UserInterface";
import { useAppSelector } from "../../store/hooks";

interface ModalProps {
  payment?: PaymentInterface;
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSaveProofOfPayment: (
    file: File,
    payment: PaymentInterface,
    user: UserInterface,
    statusID: number,
    reference?: string,
    isModification?: boolean
  ) => void;
  isModification?: boolean;
  setIsModification?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ProofOfPaymentModal: FC<ModalProps> = ({
  payment,
  openModal,
  setOpenModal,
  onSaveProofOfPayment,
  isModification,
  setIsModification,
}) => {
  const exchanges = useCurrencyExchanges();
  const [file, setFile] = useState<File>();
  const [referenceError, setReferenceError] = useState<string>("");
  const [fileError, setFileError] = useState<string>("");
  const [reference, setReference] = useState<string>("");
  const user = useAppSelector((state) => state.user.user);

  const handleFileChange = (file: File[]) => {
    // Verify that the file is valid (PDF or image)
    if (
      file &&
      (file[0].type === "application/pdf" || file[0].type.startsWith("image/"))
    ) {
      setFileError("");
      setFile(file[0]);
    } else if (!!file) {
      setFileError("El archivo debe ser formato PDF o imagen");
    }
  };

  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replaceAll(/\D/g, "");
    if (val.length > 6) val = val.slice(0, 6);
    setReference(val);

    if (val.length === 6) {
      setReferenceError("");
    } else {
      setReferenceError("El número debe tener exactamente 6 dígitos");
    }
  };

  const handleSubmit = async () => {
    if (!file) return;

    onSaveProofOfPayment(
      file,
      payment!,
      user!,
      payment?.isRetention
        ? PaymentStatusEnum.APPROVE
        : payment?.status ?? PaymentStatusEnum.PENDING,
      reference ?? payment?.reference,
      isModification
    );
    setOpenModal(false);
    setIsModification?.(false);
    setReference("");
    setFile(undefined);
  };

  useEffect(() => {
    setFileError("");
    setReferenceError("");
  }, [openModal]);

  return (
    <Modal openModal={openModal} setOpenModal={setOpenModal}>
      {/* Header */}
      <div className="flex flex-row justify-between mb-4 gap-16">
        <h2 className="text-xl font-bold">
          Subir comprobante de {payment?.isRetention ? "retención" : "pago"}{" "}
        </h2>

        <div className="ml-3 flex h-7 items-center">
          <button
            type="button"
            className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            onClick={() => {
              setOpenModal(false);
              setReference("");
              setFile(undefined);
            }}
          >
            <span className="absolute -inset-2.5" />
            <span className="sr-only">Close panel</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div>
        <div className="flex flex-col flex-1 mb-2">
          <p>{payment?.paymentMethod.paymentMethodName}</p>
          <p className="text-gray-500">
            {currencyExchangeText(payment?.amount ?? 0, exchanges, "USD")}
          </p>
          <p className="text-gray-500 text-xs">
            ({currencyExchangeText(payment?.amount ?? 0, exchanges, "BS")})
          </p>
        </div>
        <div className="flex flex-1 py-2 flex-col md:flex-row">
          {payment?.paymentMethod.paymentMethodID !==
            PaymentMethodEnum.REINTEGRO_ZELLE && (
            <div className="w-full">
              <FormTextInput
                name="proofOfPayment"
                label={
                  payment?.isRetention ? "Número de Comprobante" : "Referencia"
                }
                value={reference}
                error={referenceError}
                onChange={handleReferenceChange}
              />
            </div>
          )}
        </div>

        <FormFileUpload
          error={fileError}
          description="El archivo debe ser formato PDF o imagen"
          onSelectFile={handleFileChange}
          className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50"
        />
      </div>

      {/* Footer */}
      <div className="flex flex-row gap-4 items-center justify-between mt-8">
        <SecondaryButton
          onClick={() => {
            setOpenModal(false);
            setReference("");
            setFile(undefined);
          }}
          className="px-4 py-2"
        >
          Cancelar
        </SecondaryButton>

        <PrimaryButton
          disabled={!file || !reference || !!fileError || !!referenceError}
          onClick={handleSubmit}
          className="px-8"
        >
          Subir
        </PrimaryButton>
      </div>
    </Modal>
  );
};

export default ProofOfPaymentModal;
